<template>
    <v-text-field
        ref="autocomplete"
        type="text"
        clearable
        :class="classname"
        :id="id"
        :prepend-icon="icon"
        :placeholder="placeholder"
        :disabled="disabled"
        v-model="autocompleteText"
        :value="defaultdata"
        @focus="onFocus()"
        @blur="onBlur()"
        @change="onChange"
        @keypress="onKeyPress"
        @keyup="onKeyUp"
        @click:clear="clear"
        :rules="required ? eventLocationRules : []"
        :label="title"
        :append-icon="useGeolocation ? 'location_searching' : 'location_disabled'"
        @click:append="() => (geolocate())"
    ></v-text-field>
</template>

<script>
    const ADDRESS_COMPONENTS = {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        administrative_area_level_2: 'county',
        country: 'long_name',
        postal_code: 'short_name'
    };
    const CITIES_TYPE = ['locality', 'administrative_area_level_3'];
    const REGIONS_TYPE = ['locality', 'sublocality', 'postal_code', 'country',
        'administrative_area_level_1', 'administrative_area_level_2'];

    const INPUT_PROMPT = ['Enter a location', 'Aight, where are goin?'];
    const LOADING_INPUT = ['Attempting sceince...', 'Please hold...', 'Really... you\'re here? ...']

    function getRandomInt(min, max) {
        return Math.floor(Math.random() * (max - min)) + min;
    }
    
    export default {
        name: 'Autocomplete',
        props: {
          id: {
            type: String,
            required: true
          },
          classname: String,
          types: {
            type: String,
            default: ''
          },
          country: {
            type: [String, Array],
            default: null
          },
          geolocationOptions: {
            type: Object,
            default: null
          }, 
          title: {
            type: String, 
            default: ''
          }, 
          required: {
            type: Boolean, 
            default: false
          },
          icon: {
            type: String,
            default: 'location_on'
          }, 
          defaultdata: {
            type: String, 
            default: ''
          }, 
          disabled: {
            type: Boolean, 
            default: false
          }
        },
        data() {
            return {
                /**
                 * The Autocomplete object.
                 *
                 * @type {Autocomplete}
                 * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
                 */
                autocomplete: null,
                /**
                 * Autocomplete input text
                 * @type {String}
                 */
                autocompleteText: (this.defaultdata != '' ? this.defaultdata : ''),
                placeholder: INPUT_PROMPT[getRandomInt(0, INPUT_PROMPT.length)],
                useGeolocation: false,
                geolocation: {
                    /**
                     * Google Geocoder Objet
                     * @type {Geocoder}
                     * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
                     */
                    geocoder: null,
                    /**
                     * Filled after geolocate result
                     * @type {Coordinates}
                     * @link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates
                     */
                    loc: null,
                    /**
                     * Filled after geolocate result
                     * @type {Position}
                     * @link https://developer.mozilla.org/en-US/docs/Web/API/Position
                     */
                    position: null
                }, 
                eventLocationRules: [
                  v => !!v || 'A location is required'
                ],
            }
        },
        computed: {

        },
        watch: {
            autocompleteText: function (newVal, oldVal) {
              this.$emit('inputChange', { newVal, oldVal }, this.id);
            },
            country: function(newVal, oldVal) {
              this.autocomplete.setComponentRestrictions({
                country: this.country === null ? [] : this.country
              });
            }, 
            defaultdata(newValue, oldValue){
              this.autocompleteText = newValue;
            }
        },
        mounted: function() {
          const options = {};
          if (this.types) {
            options.types = [this.types];
          }
          if (this.country) {
            options.componentRestrictions = {
              country: this.country
            };
          }
          this.autocomplete = new window.google.maps.places.Autocomplete(
                document.getElementById(this.id),
                options
            );
          this.autocomplete.addListener('place_changed', this.onPlaceChanged);
        },
        methods: {
            /**
             * When a place changed
             */
            onPlaceChanged() {
                let place = this.autocomplete.getPlace();
                if (!place.geometry) {
                  /**
                   * User entered the name of a Place that was not suggested and
                   * pressed the Enter key, or the Place Details request failed.
                   */
                  this.$emit('no-results-found', place, this.id);
                  return;
                }
                if (place.address_components !== undefined) {
                    /** return returnData object and PlaceResult object */
                    this.$emit('placechanged', this.formatResult(place), place, this.id);
                    /** update autocompleteText then emit change event */
                    this.autocompleteText = place.name ? place.name+', '+place.formatted_address : place.formatted_address /* document.getElementById(this.id).value */
                    this.onChange()
                }
            },
            /**
             * When the input gets focus
             */
            onFocus() {
              this.biasAutocompleteLocation();
              this.$emit('focus');
            },
            /**
             * When the input loses focus
             */
            onBlur() {
              this.$emit('blur');
            },
            /**
             * When the input got changed
             */
            onChange() {
              this.$emit('change', this.autocompleteText);
            },
            /**
             * When a key gets pressed
             * @param  {Event} event A keypress event
             */
            onKeyPress(event) {
              this.$emit('keypress', event);
            },
            /**
             * When a keyup occurs
             * @param  {Event} event A keyup event
             */
            onKeyUp(event) {
              this.$emit('keyup', event);
            },
            /**
             * Clear the input
             */
            clear() {
              this.$emit('clear');
              this.autocompleteText = '';
              this.placeholder = INPUT_PROMPT[getRandomInt(0, INPUT_PROMPT.length)];
            },
            /**
             * Focus the input
             */
            focus() {
              this.$refs.autocomplete.focus()
            },
            /**
             * Blur the input
             */
            blur() {
              this.$refs.autocomplete.blur()
            },
            /**
             * Update the value of the input
             * @param  {String} value
             */
            update (value) {
              this.autocompleteText = value
            },
            /**
             * Update the coordinates of the input
             * @param  {Coordinates} value
             */
            updateCoordinates (value) {
                if (!value && !(value.lat || value.lng)) return;
                if (!this.geolocation.geocoder) this.geolocation.geocoder = new google.maps.Geocoder();
                this.geolocation.geocoder.geocode({'location': value}, (results, status) => {
                    if (status === 'OK') {
                        results = this.filterGeocodeResultTypes(results);
                        if (results[0]) {
                            this.$emit('placechanged', this.formatResult(results[0]), results[0], this.id);
                            this.update(results[0].formatted_address);
                        } else {
                            this.$emit('error', 'no result for provided coordinates');
                        }
                    } else {
                        this.$emit('error', 'error getting address from coords');
                    }
                })
            },
            /**
             * Update location based on navigator geolocation
             */
            geolocate () {
                this.useGeolocation = !this.useGeolocation; 
                if(this.useGeolocation){
                    this.placeholder = LOADING_INPUT[getRandomInt(0, INPUT_PROMPT.length)]
                    this.updateGeolocation ((geolocation, position) => {
                        this.updateCoordinates(geolocation)
                    })
                } else {
                    this.disableGeolocation()
                }
            },
            /**
             * Update internal location from navigator geolocation
             * @param  {Function} (geolocation, position)
             */
            updateGeolocation (callback = null) {
                if (navigator.geolocation) {
                    let options = {};
                    if(this.geolocationOptions) Object.assign(options, this.geolocationOptions);
                    navigator.geolocation.getCurrentPosition(position => {
                        let geolocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        };
                        this.geolocation.loc = geolocation;
                        this.geolocation.position = position;
                        if (callback) callback(geolocation, position);
                    }, err => {
                        this.$emit('error', 'Cannot get Coordinates from navigator', err);
                    }, options);
                }
            },
            disableGeolocation() {
                this.useGeolocation = false; 
                this.geolocation.loc = null, this.geolocation.position = null;
            },
            /*
              Bias the autocomplete object to the user's geographical location,
              as supplied by the browser's 'navigator.geolocation' object.
            */
            biasAutocompleteLocation () {
                if (this.enableGeolocation) {
                    this.updateGeolocation((geolocation, position) => {
                        let circle = new google.maps.Circle({
                            center: geolocation,
                            radius: position.coords.accuracy
                        });
                        this.autocomplete.setBounds(circle.getBounds());
                    })
                }
            },
            /**
             * Format result from Geo google APIs
             * @param place
             * @returns {{formatted output}}
             */
            formatResult (place) {
                let returnData = {};

                returnData['address'] = place.formatted_address;

                returnData['latitude'] = place.geometry.location.lat();
                returnData['longitude'] = place.geometry.location.lng();

                returnData['string'] = place.geometry.location.lat()+','+place.geometry.location.lng()+','+encodeURI(returnData['address']);

                return returnData
            },
            /**
             * Extract configured types out of raw result as
             * Geocode API does not allow to do it
             * @param results
             * @returns {GeocoderResult}
             * @link https://developers.google.com/maps/documentation/javascript/reference#GeocoderResult
             */
            filterGeocodeResultTypes (results) {
                if (!results || !this.types) return results;
                let output = [];
                let types = [this.types];
                if (types.includes('(cities)')) types = types.concat(CITIES_TYPE);
                if (types.includes('(regions)')) types = types.concat(REGIONS_TYPE);
                for (let r of results) {
                    for (let t of r.types) {
                        if (types.includes(t)) {
                            output.push(r);
                            break;
                        }
                    }
                }
                return output;
            }
        }
    }
</script>